import React, { useState } from 'react';
import {
  Globe, Server, ShieldCheck, Code2, Database, Image, Network, Plug,
  Archive, FileText, Download, KeyRound, ExternalLink, AlertTriangle,
} from 'lucide-react';

/**
 * 交接收據（Handover）—— Admin 專屬（isAdmin 才渲染）
 * 對應客人 2026-08-27 交收清單：Domain／Hosting／管理員帳戶／Source Code／Database／
 * 素材原檔／DNS・SSL／第三方服務授權／Backup／技術文件／權益聲明。
 * 保安原則：機密值（API keys／密碼）一律不在頁面顯示，只列服務名稱、存放位置與輪替指引。
 */

const DownloadLink: React.FC<{
  apiBase: string; token: string; kind: string; label: string; filename: string;
}> = ({ apiBase, token, kind, label, filename }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/admin/handover/download/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(`下載失敗：${e instanceof Error ? e.message : String(e)}`);
    }
    setBusy(false);
  };
  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      className="inline-flex items-center gap-1 rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700 transition hover:bg-blue-100 disabled:opacity-50"
    >
      <Download className="h-3 w-3" /> {busy ? '準備中…' : label}
    </button>
  );
};

const apiBasePlaceholder = null;

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-slate-900">
      <span className="text-blue-800">{icon}</span>
      {title}
    </h3>
    <div className="space-y-2 text-sm leading-relaxed text-slate-700">{children}</div>
  </section>
);

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex flex-col gap-0.5 border-b border-slate-100 py-1.5 last:border-0 md:flex-row md:gap-3">
    <span className="w-44 shrink-0 font-semibold text-slate-500">{k}</span>
    <span className="min-w-0 break-all">{v}</span>
  </div>
);

const Pending: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-bold text-amber-800">
    <AlertTriangle className="h-3 w-3" /> 待議定：{children}
  </span>
);

const HandoverPanel: React.FC<{ apiBase: string; token: string }> = ({ apiBase, token }) => {
  const [exporting, setExporting] = useState(false);

  const downloadBackup = async () => {
    setExporting(true);
    try {
      // 完整備份：全站內容 JSON（物業/設定/消息/團隊/查詢）＋ 媒體清單
      const [cmsRes, mediaPages] = await Promise.all([
        fetch(`${apiBase}/api/cms/data`),
        (async () => {
          const all: unknown[] = [];
          let page = 1;
          for (;;) {
            const r = await fetch(`${apiBase}/api/media?page=${page}&limit=100`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) break;
            const d = await r.json();
            all.push(...(d.media || []));
            if (page >= (d.pagination?.totalPages ?? 1)) return all;
            page++;
          }
        })(),
      ]);
      const cms = await cmsRes.json();
      const payload = {
        exportedAt: new Date().toISOString(),
        system: 'Marveland CMS (Cloudflare Pages + Workers + KV/R2)',
        note: '媒體檔案本體存放於 R2（marveland-media），可經各 media.url 逐一下載；此備份含全部內容資料與媒體清單。',
        cmsData: cms,
        mediaList: mediaPages,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `marveland-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900">交接收據（Handover）</h2>
        <p className="mt-1 text-sm text-slate-500">
          對應客人交收清單逐項列出。機密值（密碼／API Keys）基於保安不於頁面顯示，僅列存放位置與輪替方式。
          標示 <Pending> </Pending> 的項目需與客人議定歸屬／交付方式。
        </p>
      </div>

      <Section icon={<Globe className="h-5 w-5" />} title="1. Domain（網域）">
        <Row k="正式網域" v={<a href="https://marveland.hk" target="_blank" rel="noreferrer" className="text-blue-700 underline">https://marveland.hk</a>} />
        <Row k="跳轉網域" v="marveland.pages.dev（Cloudflare Pages 預設域）" />
        <Row k="DNS 託管" v="Cloudflare（Proxy 開啟，隱藏源站）" />
        <Row k="註冊商／轉移碼" v={<Pending>提供註冊商名稱與 EPP 轉移碼，或議定保留原註冊商由貴公司接管帳戶</Pending>} />
      </Section>

      <Section icon={<Server className="h-5 w-5" />} title="2. Hosting（託管架構）">
        <Row k="前端" v="Cloudflare Pages — 專案 marveland（靜態 React + Vite）" />
        <Row k="後端 API" v="Cloudflare Workers — marveland-cms.jimsbond007.workers.dev" />
        <Row k="資料庫" v="Cloudflare KV（CMS_DATA：全站內容）＋ Durable Object（MediaDO：媒體索引）" />
        <Row k="檔案儲存" v="Cloudflare R2 — marveland-media（照片）／marveland-bgm（背景音樂）" />
      </Section>

      <Section icon={<KeyRound className="h-5 w-5" />} title="3. 網站最高管理員帳戶">
        <Row k="超級管理員" v="帳號 Marveland（Admin 角色，全權限）— 密碼由貴公司管理層保管；遺失可經 Cloudflare Secrets（ADMIN_PASSWORD）重設" />
        <Row k="Staff 帳戶" v="後台「團隊帳號」分頁可見全部帳號（Billy、Faye、may 等），密碼由各同事自管（後台可自助修改）" />
        <Row k="權限模型" v="Admin＝全站管理；Staff＝僅管理 createdBy 為自己的物業" />
      </Section>

      <Section icon={<Code2 className="h-5 w-5" />} title="4. 完整 Source Code">
        <Row k="代碼庫" v="Git（monorepo：apps/web 前端＋apps/cms-worker 後端）" />
        <Row k="交付內容" v={<DownloadLink apiBase={apiBase} token={token} kind="source" label="下載最新完整源碼（ZIP，含前後端＋技術文件）" filename="marveland-source-latest.zip" />} />
        <Row k="技術棧" v="React 18 + Vite + Tailwind（前端）；Hono + Cloudflare Workers API（後端）；TypeScript 全棧" />
      </Section>

      <Section icon={<Database className="h-5 w-5" />} title="5. Database（資料庫）">
        <Row k="內容資料" v="Cloudflare KV namespace CMS_DATA（key：cms_data——全部物業/設定/消息/團隊；另有 30 日自動歷史快照 cms_history_*）" />
        <Row k="媒體索引" v="MediaDO Durable Object（storage：media_index／media_usage）＋ media:item:* 元資料" />
        <Row k="流量統計" v="KV analytics:property:{id}（views/fbClicks）" />
        <Row k="完整備份" v="見第 9 節一鍵下載" />
      </Section>

      <Section icon={<Image className="h-5 w-5" />} title="6. 網站素材原檔">
        <Row k="照片" v="R2 marveland-media 桶（167 張，已優化壓縮）；清單與下載 URL 見「完整備份 JSON」內 mediaList" />
        <Row k="品牌素材" v="Logo／字體（Noto Sans TC）／BGM 四首——R2 marveland-bgm 及媒體庫" />
        <Row k="水印規格" v="中央對角 MARVELAND（自動套用於所有上傳照片，防盜用）" />
      </Section>

      <Section icon={<Network className="h-5 w-5" />} title="7. DNS／SSL 設定">
        <Row k="SSL" v="Cloudflare Universal SSL（自動簽發自動續期，現行有效）" />
        <Row k="DNS 紀錄" v="marveland.hk → Cloudflare Pages（CNAME flattening）；marveland-cms.workers.dev 由 Workers 管理自訂域 video.jkdcoding.com 另屬 JKD 系統" />
      </Section>

      <Section icon={<Plug className="h-5 w-5" />} title="8. 第三方服務帳戶及授權">
        <p className="text-xs text-slate-500">以下服務的 API Key／Secret 全部存放於 Cloudflare Worker Secrets（明文不顯示）。轉移時可在 Cloudflare Dashboard → Workers → marveland-cms → Settings → Variables 查看名單，並於各家服務台輪替為貴公司自有帳戶的新 Key。</p>
        <Row k="Facebook 專頁發佈" v="FB_PAGE_ID／FB_PAGE_ACCESS_TOKEN／FB_APP_ID／FB_APP_SECRET —— 授權發文至金晉物業代理專頁；長期 Token 與 Meta App 綁定，轉移時建議貴公司自建 Meta App 重新產生" />
        <Row k="Shotstack" v="SHOTSTACK_API_KEY —— 影片渲染引擎（照片轉影片）" />
        <Row k="阿里雲 ICE＋OSS" v="ICE_ACCESS_KEY_ID／SECRET／ICE_OSS_OUTPUT_BASE（輸出桶 marveland-vedio，北京）—— AI 全製作管線合成" />
        <Row k="智譜 BigModel" v="ZHIPUAI_API_KEY —— CogVideoX 實景生成；DASHSCOPE_API_KEY —— 通義圖像（預留）" />
      </Section>

      <Section icon={<Archive className="h-5 w-5" />} title="9. 完整 Backup">
        <button
          onClick={downloadBackup}
          disabled={exporting}
          className="mb-2 inline-flex items-center gap-2 rounded-lg bg-blue-800 px-4 py-2 text-sm font-bold text-white hover:bg-blue-900 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {exporting ? '匯出中…' : '下載完整備份（JSON：全站內容＋媒體清單）'}
        </button>
        <p className="text-xs text-slate-500">
          內含全部物業、網站設定、消息、團隊、查詢記錄及 167 張媒體的下載清單。照片本體可由清單內每個 media.url 批次下載。
          建議交收日下載一次存檔；日後亦可隨時再匯出最新狀態。
        </p>
      </Section>

      <Section icon={<FileText className="h-5 w-5" />} title="10. 相關技術文件">
        <Row k="建置全記錄" v={<span>Docs/BUILD_LOG.md（37 輪迭代）　<DownloadLink apiBase={apiBase} token={token} kind="build_log" label="下載" filename="BUILD_LOG.md" /></span>} />
        <Row k="維護手冊" v={<span>Docs/PROMPT_MAINTAIN.md　<DownloadLink apiBase={apiBase} token={token} kind="maintain" label="下載" filename="PROMPT_MAINTAIN.md" /></span>} />
        <Row k="需求文件" v={<span>Docs/PRD.md　<DownloadLink apiBase={apiBase} token={token} kind="prd" label="下載" filename="PRD.md" /></span>} />
        <Row k="系統架構" v={<span>Docs/SA&D.md　<DownloadLink apiBase={apiBase} token={token} kind="sad" label="下載" filename="SA&D.md" /></span>} />
        <Row k="框架複製指南" v={<span>Docs/FRAMEWORK_CLONE_GUIDE.md　<DownloadLink apiBase={apiBase} token={token} kind="clone_guide" label="下載" filename="FRAMEWORK_CLONE_GUIDE.md" /></span>} />
        <Row k="員工操作手冊" v={<a href="https://marveland.hk/manual" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 underline">marveland.hk/manual <ExternalLink className="h-3 w-3" /></a>} />
        <Row k="下載" v="每份文檔可直接點擊下載（下方各連結）" />
      </Section>

      <Section icon={<ShieldCheck className="h-5 w-5" />} title="11. 權益聲明（客人要求確認事項）">
        <div className="rounded-lg bg-green-50 p-4 text-sm leading-relaxed text-green-900">
          <p>完成付款後，網站成品及相關<strong>使用權／轉移權</strong>歸金晉物業代理（以下稱貴公司）所有；貴公司日後可<strong>自行或委託任何第三方網站公司</strong>對網站進行搬遷、修改及維護，我方不設鎖定條款。涉及第三方帳戶（Cloudflare／Meta／Shotstack／阿里雲／智譜）之實際控制權轉移，按第 2／7／8 節所議定方式配合辦理，並於交收時完整提供所需資料與必要技術協助。</p>
        </div>
        <p className="text-xs text-slate-400">* 正式聲明文字以雙方合約／交收文件為準，此頁為技術清單對照。</p>
      </Section>
    </div>
  );
};

export default HandoverPanel;
